import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  SignOut, Waveform, User as UserIcon, CaretDown, Crown, GearSix, Ticket, FolderOpen,
} from "@phosphor-icons/react";
import StreakPill from "./StreakPill";
import useStats from "../lib/useStats";
import { APP_VERSION } from "../version";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef(null);
  const { stats } = useStats();

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <nav
      className="w-full border-b rule bg-white/80 backdrop-blur-xl sticky top-0 z-30"
      data-testid="navbar"
      style={{
        // Respect the device's top safe area (iOS notch, Android status bar).
        // Without this, the Sign-in button sits behind the battery/wifi icons
        // when Ekkol runs inside the Android webview (Capacitor) or iOS
        // standalone PWA. env() falls back to 0px on desktop browsers — safe.
        paddingTop: "env(safe-area-inset-top, 0px)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="brand-link">
          <Waveform size={22} weight="duotone" />
          <span className="font-serif text-2xl leading-none">Ekkol</span>
          <span className="eyebrow ml-3 hidden md:inline" data-testid="app-version">v {APP_VERSION}</span>
        </Link>
        <div className="flex items-center gap-6">
          {user ? (
            <>
              <StreakPill stats={stats} />
              <Link to="/dashboard" className="link-underline text-sm" data-testid="nav-dashboard">Library</Link>
              <Link to="/decks/new" className="link-underline text-sm hidden sm:inline" data-testid="nav-new">New deck</Link>
              {(user.is_admin || user.plan === "edu" || user.plan === "team" || user.show_classes_nav || user.has_any_class) && (
                <Link to="/classes" className="link-underline text-sm hidden sm:inline" data-testid="nav-classes">Classes</Link>
              )}
              <Link to="/pricing" className="link-underline text-sm hidden md:inline" data-testid="nav-pricing">Pricing</Link>
              {user.is_admin && (
                <Link to="/admin" className="link-underline text-sm hidden md:inline text-[color:var(--oxford)]" data-testid="nav-admin">Admin</Link>
              )}
              <div className="relative" ref={ref}>
                <button
                  onClick={() => setMenuOpen((o) => !o)}
                  className="flex items-center gap-2 text-sm text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
                  data-testid="nav-user-menu"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full border rule" />
                  ) : (
                    <span className="w-8 h-8 rounded-full border rule bg-[color:var(--surface-alt)] inline-flex items-center justify-center">
                      <UserIcon size={14}/>
                    </span>
                  )}
                  <CaretDown size={12} className={`transition-transform ${menuOpen ? "rotate-180" : ""}`}/>
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-60 bg-white border rule shadow-lg fade-in" role="menu" data-testid="nav-user-menu-dropdown">
                    <div className="p-4 border-b rule">
                      <div className="text-sm font-medium truncate">{user.name}</div>
                      <div className="text-xs text-[color:var(--ink-muted)] truncate">{user.email}</div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-[10px] font-mono uppercase tracking-widest border rule px-2 py-0.5">
                          {user.plan === "plus" ? "Plus" : (user.plan === "edu" || user.plan === "team") ? "Edu" : "Free"}
                        </span>
                        {user.is_super && <span className="text-[10px] font-mono uppercase tracking-widest border rule px-2 py-0.5 bg-[color:var(--oxford)] text-white">Super admin</span>}
                        {!user.is_super && user.is_admin && <span className="text-[10px] font-mono uppercase tracking-widest border rule px-2 py-0.5">Admin</span>}
                      </div>
                    </div>
                    <Link to="/profile" onClick={() => setMenuOpen(false)} className="block px-4 py-3 text-sm hover:bg-[color:var(--surface-alt)] flex items-center gap-2" data-testid="menu-profile">
                      <GearSix size={14}/> Profile & settings
                    </Link>
                    <Link to="/pricing" onClick={() => setMenuOpen(false)} className="block px-4 py-3 text-sm hover:bg-[color:var(--surface-alt)] flex items-center gap-2 border-t rule" data-testid="menu-pricing">
                      <Crown size={14}/> Plans & pricing
                    </Link>
                    <Link to="/profile#promo" onClick={() => setMenuOpen(false)} className="block px-4 py-3 text-sm hover:bg-[color:var(--surface-alt)] flex items-center gap-2 border-t rule" data-testid="menu-promo">
                      <Ticket size={14}/> Redeem code
                    </Link>
                    {user.is_admin && (
                      <Link to="/admin/vault" onClick={() => setMenuOpen(false)} className="block px-4 py-3 text-sm hover:bg-[color:var(--surface-alt)] flex items-center gap-2 border-t rule text-[color:var(--oxford)]" data-testid="menu-vault">
                        <FolderOpen size={14}/> Document vault
                      </Link>
                    )}
                    <button
                      onClick={async () => { setMenuOpen(false); await logout(); navigate("/"); }}
                      className="w-full text-left px-4 py-3 text-sm hover:bg-[color:var(--surface-alt)] flex items-center gap-2 border-t rule text-[color:var(--vermilion)]"
                      data-testid="menu-logout"
                    >
                      <SignOut size={14}/> Sign out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/pricing" className="link-underline text-sm hidden md:inline" data-testid="nav-pricing-public">Pricing</Link>
              <Link to="/login" className="btn-primary" data-testid="nav-login">Sign in</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
